// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  // Check if window is defined (not available during SSR)
  if (marketplaceRootURL) {
    return marketplaceRootURL.replace(/\/$/, '');
  } else if (typeof window !== 'undefined') {
    return `${window.location.origin}`;
  } else {
    // During SSR, return empty string as we can't determine the origin
    return '';
  }
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : body ? { body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  // Check if window.fetch is available (not available during SSR)
  if (typeof window === 'undefined' || !window.fetch) {
    // During SSR, we can't make client-side fetch requests
    return Promise.reject(new Error('Fetch is not available during server-side rendering'));
  }

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Fetch top advisors users from the local API endpoint.
//
// See `server/api/users/top-advisors.js` to see what data should
// be sent in the body.
export const fetchTopAdvisors = ({ perPage = 12, page = 1 } = {}) =>
  request(`/api/users/top-advisors?perPage=${perPage}&page=${page}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

// Fetch all story master experts (both top advisors and regular category masters) from the local API endpoint.
//
// See `server/api/users/all-experts.js` to see what data should
// be sent in the body.
export const fetchAllExperts = ({ perPage = 100, page = 1 } = {}) =>
  request(`/api/users/all-experts?perPage=${perPage}&page=${page}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

// Fetch VocaTales Experts from the local API endpoint.
//
// See `server/api/users/masters.js` to see what data should
// be sent in the body.
export const fetchMasters = ({ perPage = 12, page = 1 } = {}) =>
  request(`/api/users/masters?perPage=${perPage}&page=${page}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

// Lookup user by slug or UUID
//
// This endpoint resolves a user slug to their UUID.
// If the identifier is already a UUID, it returns it directly.
//
// See `server/api/users/lookup-by-slug.js` for the server implementation.
export const lookupUserBySlug = identifier =>
  request(`/api/users/lookup/${encodeURIComponent(identifier)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// Update the custom listing order for the current user.
// The order is stored in user's profile.publicData.liveListingOrder
// and controls how live listings appear on the user's profile page.
//
// See `server/api/listings/update-order.js` for the server implementation.
export const updateListingOrder = (listingOrder, userId = null) => {
  const body = userId ? { listingOrder, userId } : { listingOrder };
  return request('/api/listings/update-order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};
